import axios from 'axios';
import { useEffect, useState } from 'react';

import { useCommonOptionsStore } from '../store';

import type { EnvType, MappingServiceResponse } from '../@types';

interface MappingServiceApiResponse {
  island_id: string;
  island_mfd_base_url: string;
  island_crm_base_url: string;
  island_api_base_url: string;
}

const MappingServiceUrl: Record<EnvType, string> = {
  prod: 'https://api.cloudbeds.com/mapping/v1.0/mfd/property?id=',
  stage: 'https://api.cloudbeds-stage.com/mapping/v1.0/mfd/property?id=',
  dev: 'https://api.cloudbeds-stage.com/mapping/v1.0/mfd/property?id=',
  local: 'https://api.cloudbeds-stage.com/mapping/v1.0/mfd/property?id=',
};

const DefaultErrorMessage =
  'Mapping service returned error, check Network tab for details.';

const DefaultMappingUrls = {
  apiUrl: 'https://api.cloudbeds.com',
  baseUrl: 'https://hotels.cloudbeds.com',
};

const localMappingServiceDefaults = {
  data: {
    island_id: 'us2',
    island_mfd_base_url: 'https://hotels.cloudbeds-local.com/',
    island_crm_base_url: 'https://wwwcrm.cloudbeds-local.com/',
    island_api_base_url: 'https://api.cloudbeds-local.com/',
  },
};

const queryMappingService = async (
  propertyId: number,
  env: EnvType,
): Promise<MappingServiceResponse | null> => {
  try {
    const baseUrlStored = localStorage.getItem('__paym_sdk_base_url');
    const apiUrlStored = localStorage.getItem('__paym_sdk_api_url');

    // Return URLs in localStorage, if present, with no need to call mapping service.
    if (baseUrlStored && apiUrlStored) {
      return Promise.resolve({ baseUrl: baseUrlStored, apiUrl: apiUrlStored });
    }

    // Grab from mapping service if not found in localStorage
    const { data }: { data: MappingServiceApiResponse } =
      env === 'local'
        ? localMappingServiceDefaults
        : await axios.get(`${MappingServiceUrl[env]}${propertyId}`);

    const { island_mfd_base_url: baseUrl, island_api_base_url: apiUrl } = data;
    return {
      baseUrl: baseUrlStored || baseUrl,
      apiUrl: apiUrlStored || apiUrl.replace('us2.', ''),
    };
  } catch (e) {
    console.error(`[${env}] Error while connecting to mapping service.`);
    console.error(e);
    return null;
  }
};

export const useMappingService = (propertyId: number, env: EnvType) => {
  const [loading, setLoading] = useState(true);
  const { apiUrl, baseUrl, setApiUrl, setBaseUrl } = useCommonOptionsStore();

  useEffect(() => {
    (async () => {
      try {
        if (!apiUrl || !baseUrl) {
          const urls = await queryMappingService(propertyId, env);
          if (!urls) throw Error(DefaultErrorMessage);
          setApiUrl(urls.apiUrl);
          setBaseUrl(urls.baseUrl);
        }
      } catch (err) {
        setApiUrl(
          localStorage.getItem('__paym_sdk_api_url') ||
            DefaultMappingUrls.apiUrl,
        );
        setBaseUrl(
          localStorage.getItem('__paym_sdk_base_url') ||
            DefaultMappingUrls.baseUrl,
        );
        console.error(`[mapping.ts] useMappingService - error:`, err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return !loading ? { apiUrl, baseUrl } : null;
};
